import type { IWebCOOSCameraPageFiltered } from "@/services/assets/services"
import type { IWebCOOSAssetSummaryView } from "@/services/assets/types"
import { Button, SelectInput, Tabs, Tooltip, utils, type ISelectOptionProps } from "@axdspub/axiom-ui-utilities"
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { useAtom } from "jotai"
import filterAtom from "./filterAtom"
import { Link, useNavigate } from "react-router"

import { toZonedTime, format } from 'date-fns-tz';
import { SortedIcon } from "@/Components/SortedIcon"
import sortAtom from "./sortAtom"
import LoadedMap from "@/Components/Map/LoadedMap"
import { serviceProductToDisplayName, serviceSlugAndLabelToProduct } from "@/services/assets/parsers"
import ImageLoader from "@/Components/Media/ImageLoader"
import type { ReactElement } from "react"
import { IconSignal } from "@/Components/Icon"
import { CameraIcon, VideoIcon } from "lucide-react"
import { useState } from "react";
import { getLegendItemForAsset, ucFirst } from "@/lib/utils"


const defaultTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
const formatInTimeZone = (date: string | number, fmt: string, tz: string) => format(toZonedTime(date, tz), fmt, { timeZone: tz });



const CameraPreviewImage = ({ rectSmall, squareSmall, assetLabel }: { rectSmall?: string; squareSmall?: string; assetLabel: string }): ReactElement => {
  return <div>
    <ImageLoader
      url={[rectSmall, squareSmall]}
      className='rounded shadow'
      errorClassName='h-16 lg:h-24'
      width={200}
      height={100}
      label={assetLabel}
    />
  </div>

}

const CameraFilters = ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {
  const [filters, setFilters] = useAtom(filterAtom)
  const filterViews = [
    {
      label: 'Region',
      id: 'region-filter',
      column: 'asset_region',
      options: data.regions
    },
    {
      label: 'State',
      id: 'state-filter',
      column: 'asset_state_or_territory',
      options: data.states
    },
    {
      label: 'Media type',
      id: 'media-type-filter',
      column: 'asset_media_types',
      options: data.imageTypes
    },
    {
      label: 'Product',
      id: 'product-filter',
      column: 'asset_service_slugs',
      options: data.products
    },
    {
      label: 'Disposition',
      id: 'disposition-filter',
      column: 'asset_disposition_slug',
      options: data.dispositions.map(d => ({ value: d.value, label: ucFirst(d.label) }))
    },
    {
      label: 'Status',
      id: 'status-filter',
      column: 'asset_operational_status_slug',
      options: data.statuses.map(s => ({ value: s.value, label: ucFirst(s.label) }))
    }
  ]
  // Add state for modal visibility and a simple mobile check
  const [showModal, setShowModal] = useState(false);

  const ClearFilters = (
    Object.values(filters).filter(f => f !== null).length > 0 &&
    <span className='cursor-pointer -mt-1 text-2xl font-bold text-red-500 hover:text-red-700' onClick={() => {
      setFilters({})
      const url = new URL(window.location.href)
      url.searchParams.delete('filters')
    }}>
      <Tooltip content='Clear all filters' dark={true} contentClassName="z-50">&times;</Tooltip>
    </span>
  )

  const FiltersContent = (
    <>
      <p className='font-bold'>Filters</p>
      {
        filterViews.map(fv => (
          <div className='flex flex-row gap-2 text-sm align-middle' key={fv.id}>
            <SelectInput
              label={fv.label}
              defaultWrapperClassName="flex flex-row gap-2"
              className={`border-2 rounded-sm shadow-sm ${filters?.[fv.column] ? 'border-primary' : ''}`}
              id={fv.id}
              testId={fv.id}
              options={fv.options as ISelectOptionProps[]}
              size="xs"
              value={filters?.[fv.column] ?? undefined}
              onChange={v => {
                const newFilters = { ...filters }
                if (v?.value !== '' && v?.value !== null && v?.value !== undefined) {
                  newFilters[fv.column] = v?.value as string
                } else {
                  delete newFilters[fv.column]
                }
                setFilters(newFilters)
              }}
            />
          </div>
        ))
      }
      {ClearFilters}

    </>
  );

  return (
    <>
      <div className='md:hidden right-0'>
        <div className="flex flex-row gap-2 pt-4 pb-2 px-4 sticky top-11 z-10">
          <Button
            className="bg-primary text-white px-4 py-2 rounded shadow font-semibold"
            size='xs'
            onClick={() => setShowModal(true)}
          >
            Filters ({Object.values(filters).filter(f => f !== null).length})
          </Button>
          {ClearFilters}

        </div>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-md relative">
              <button
                className="absolute top-2 right-2 text-2xl font-bold text-slate-400 hover:text-slate-600"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
              <div className="flex flex-col gap-4">{FiltersContent}</div>
              <button
                className="mt-4 w-full bg-primary text-white py-2 rounded"
                onClick={() => setShowModal(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='hidden md:flex flex-row gap-8 pt-6 pb-4 -mx-10 px-10 bg-white sticky top-11 z-10 '>
        {FiltersContent}
      </div>

    </>
  )

}

const min = 60 * 1000
const hr = min * 60
const day = 24 * hr
const week = 7 * day
const formatEndDateLabel = (date: Date, relativeTo: Date = new Date()): string | null => {
  const diff = relativeTo.getTime() - date.getTime()
  if (diff < min * 10) {
    return '< 10 minutes ago'
  } else if (diff < 30 * min) {
    return '< 30 minutes ago'
  } else if (diff < hr) {
    return '< an hour ago'
  } else if (diff < day) {
    return '< a day ago'
  } else if (diff < week) {
    return '< a week ago'
  } else {
    return null
  }

}



export const CamerasTable = ({
  data,
  className,
  defaultClassName = 'w-full border-collapse',
  theadClassName,
  defaultTheadClassName = 'sticky top-27 md:top-43 bg-white shadow-md z-40'
}: {
  data: IWebCOOSCameraPageFiltered,
  className?: string,
  defaultClassName?: string,
  theadClassName?: string,
  defaultTheadClassName?: string

}): ReactElement => {

  const navigate = useNavigate()
  const [sorts, setSorts] = useAtom(sortAtom)
  const columnHelper = createColumnHelper<IWebCOOSAssetSummaryView & {
    asset_media_types?: string[]
  }>()
  const getNextSortOb = (id: string): { column: string; dir: 'asc' | 'desc' } | null => {
    const existingDir = sorts.find(s => s.column === id)?.dir ?? 'desc'
    return {
      column: id,
      dir: existingDir === 'desc' ? 'asc' : 'desc'
    }
  }
  const columns = [
    columnHelper.accessor('asset_thumbnails', {
      header: '',
      size: 200,
      minSize: 200,
      enableSorting: false,
      cell: info => {
        const row = info.row.original
        const rectSmall = row?.asset_thumbnails?.rect_small
        const squareSmall = row?.asset_thumbnails?.square_small
        return <div className='min-w-[150px]'>
          <CameraPreviewImage rectSmall={rectSmall} squareSmall={squareSmall} assetLabel={row.asset_label} />
        </div>
      }
    }),
    columnHelper.accessor('asset_label', {
      header: () => {
        return <p onClick={() => {
          const nextSort = getNextSortOb('asset_label')
          if (nextSort === null) {
            setSorts(sorts.filter(sc => sc.column !== 'asset_label'))
            return
          }
          const newSorts = sorts.filter(sc => sc.column !== 'asset_label')
          newSorts.push(nextSort)
          setSorts(newSorts)
        }}>Camera</p>
      },
      size: 200,
      enableSorting: true,
      cell: info => {

        return <>
          <p>{info.getValue()}</p>
        </>
      }
    }),

    columnHelper.accessor('asset_region', {
      header: 'Region',
      enableSorting: true,
      cell: info => {
        const row = info.row.original
        return <>
          <p>{row.asset_region_label ?? row.asset_region ?? 'NA'}</p>

        </>
      }
    }),
    columnHelper.accessor('asset_state_or_territory', {
      header: 'State',
      enableSorting: true,
      cell: info => {
        const row = info.row.original
        return <>
          <p>{row.asset_state_or_territory ?? 'NA'}</p>

        </>
      }
    }),
    /* columnHelper.accessor('asset_slug', {
        header: 'Data Access Slug',
        enableSorting: true,
        cell: info => <div className='hover:border-red-200 hover:border-2 h-full' onClick={(e)=>{
          e.preventDefault()
          e.stopPropagation()
          
        }}>
          {info.getValue()}
        </div>
    }), */
    columnHelper.accessor('asset_service_slugs', {
      header: 'Products',
      enableSorting: false,
      minSize: 300,
      cell: info => <div className='flex flex-row flex-wrap gap-2 items-center justify-center'>{Object.values(
        Object.fromEntries(
          info.getValue().map((slug, index) => {
            const label = info.row.original.asset_service_labels[index]
            const product = serviceSlugAndLabelToProduct(slug, label ?? slug);
            return product ? { product, service: info.row.original.asset_service_uuids[index] } : null;
          })
            .filter((p) => p !== null)
            .map(p => [p.product, p])
        )
      )
        .map(ob => <Tooltip key={ob.product} dark={true} content={`${serviceProductToDisplayName(ob.product)} media`} className="flex-none">
          <Link to={`/cameras/${info.row.original.asset_slug}?service=${ob.service}`}>
            {
              ob.product === 'seals'
                ? <span className='text-xs bg-primary p-2 rounded-md text-white whitespace-nowrap'>Seal monitoring</span>
                : <img src={`/${ob.product}.svg`} className='w-10 h-10' />
            }

          </Link>
        </Tooltip>)
      }</div>
    }),
    columnHelper.accessor('asset_media_types', {
      header: 'Media Types',
      enableSorting: false,
      cell: info => {
        const row = info.row.original
        const hasStreamingVideo = row.asset_service_types.includes('webcoos.streamingservice')
        const hasNearRealTimeVideo = row.asset_product_slugs.includes('video-archive')
        return <div className='flex flex-row gap-2 items-center justify-center'>
          {hasStreamingVideo && <Tooltip dark={true} content="Streaming video">
            <Link to={`/cameras/${row.asset_slug}`}>
              <span className='p-2 bg-primary text-white rounded-md inline-block shadow-sm'>
                <VideoIcon className='w-4 h-4' />
              </span>
            </Link>
          </Tooltip>
          }
          {hasNearRealTimeVideo && <Tooltip dark={true} content="Near real-time video">
            <Link to={`/cameras/${row.asset_slug}?service=${row.asset_service_slugs.find(s => s.search('video-archive') !== -1)}`}>
              <span className='p-2 bg-slate-400 text-white rounded-md inline-block shadow-sm'>
                <VideoIcon className='w-4 h-4' />
              </span>
            </Link>
          </Tooltip>}
          <Tooltip dark={true} content="Snapshots">
            <Link to={`/cameras/${row.asset_slug}`}>
              <CameraIcon className='w-6 h-6' />
            </Link>
          </Tooltip>
        </div >
      }
    }),
    columnHelper.accessor('asset_disposition_slug', {
      header: 'Disposition',
      enableSorting: true,
      cell: info => {
        const legendItem = getLegendItemForAsset(info.row.original)
        return <p className='flex flex-row gap-1 items-center'><span className={`rounded-4xl w-3 h-3 border-1 ${legendItem?.border} ${legendItem?.color}`}></span> {ucFirst(String(info.getValue()))}</p>
      },
    }),
    columnHelper.accessor('asset_operational_status_label', {
      header: 'Status',
      enableSorting: true,
      cell: (info) => {
        const status = info.getValue().toLowerCase()
        return <div className='text-center align-middle'>
          <Tooltip className='mx-auto' dark={true} content={`${info.row.original.asset_operational_status_note ?? info.getValue()} as of ${info.row.original.asset_operational_status_as_of ? formatInTimeZone(info.row.original.asset_operational_status_as_of, 'yyyy-MM-dd HH:mm', info.row.original.asset_timezone ?? defaultTimeZone) : 'unknown date'}`} >
            <span
              className={utils.makeClassName({
                defaultClassName: 'group rounded uppercase py-1 relative text-xs mx-auto',
                extras: [
                  status === 'active' ? 'bg-primary text-white px-3' : undefined,
                  status !== 'active' ? 'px-3 bg-slate-200' : undefined,
                  status === 'not checked' ? 'text-slate-400' : undefined,
                ]
              })}
            >
              {
                status === 'live' && (
                  <IconSignal
                    size={4}
                    paddingx={0}
                    extraClasses='pr-1 inline-block align-text-top'
                  />
                )
              }
              {status}
            </span>
          </Tooltip>
        </div>
      }
    }),
    columnHelper.accessor('asset_first_starting', {
      header: 'Starting',
      minSize: 100,
      enableSorting: true,
      cell: info => {
        return <p className='min-w-[100px]'>{
          info.row.original.asset_first_starting === null
            ? 'NA'
            : formatInTimeZone(
              info.row.original.asset_first_starting,
              'yyyy-MM-dd',
              info.row.original.asset_timezone ?? defaultTimeZone
            )
        }</p>
      }
    }),
    columnHelper.accessor('asset_last_ending', {
      header: 'Newest media',
      enableSorting: true,
      minSize: 120,
      cell: info => {
        if (info.row.original.asset_last_ending === null) {
          return <p>NA</p>
        }
        const date = new Date(info.row.original.asset_last_ending)
        const label = formatEndDateLabel(date)
        const formattedDate = formatInTimeZone(
          Number(date),
          `yyyy-MM-dd${label ? ' HH:mm' : ''}`,
          info.row.original.asset_timezone ?? defaultTimeZone
        )
        return <div className='flex flex-col gap-2 min-w-[120px]'>
          {
            label ? <>
              <p>{label}</p>
              <p className='text-xs text-slate-400'>{formattedDate}</p>
            </>
              : <p>{formattedDate}</p>
          }
        </div>
      }
    }),
  ]
  const table = useReactTable({
    data: data.assets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    autoResetAll: false
  })



  const sortsMap = Object.fromEntries(sorts.map(s => [s.column, s]))

  return (

    <table className={
      utils.makeClassName({
        className,
        defaultClassName
      })
    }>
      <thead className={
        utils.makeClassName({
          className: theadClassName,
          defaultClassName: defaultTheadClassName
        })
      }>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, index) => {
              const isSortable = header.column.columnDef.enableSorting === true
              const isSortedOn = isSortable && sortsMap[header.id] !== undefined
              return (
                <th key={header.id} className={`${index === 1 ? 'sticky left-0 z-5 ' : ''}p-4 text-left text-sm first:pl-10 last:pr-10 ${isSortable ? `cursor-pointer text-slate-600 hover:text-slate-800${isSortedOn ? ' bg-slate-100' : ' hover:bg-slate-100 bg-white'}` : 'text-slate-400'}`} style={{ width: header.getSize() }} onClick={isSortable ? () => {
                  const nextSort = getNextSortOb(header.column.id)
                  if (nextSort === null) {
                    setSorts(sorts.filter(sc => sc.column !== header.column.id))
                    return
                  }
                  setSorts([nextSort])
                } : undefined}>
                  <div className='flex flex-row gap-1 justify-between items-center'>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    {
                      isSortable
                        ? <SortedIcon activeColor='#333' direction={sortsMap[header.id] === undefined ? 'none' : sortsMap[header.id].dir === 'desc' ? 'desc' : 'asc'} />
                        : ''
                    }
                  </div>
                </th>
              )
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            className='odd:bg-slate-100 even:bg-white hover:bg-slate-50 cursor-pointer'
            data-asset-slug={row.original.asset_slug}
            onClick={(e) => {
              const target = e.target as HTMLElement
              if (target.tagName !== 'A' && !target.closest('a')) {
                const url = `/cameras/${row.original.asset_slug}`
                navigate(url)
              }
            }}

          >
            {row.getVisibleCells().map((cell, index) => (
              <td key={cell.id} className={`p-4 align-top first:pl-10 last:pr-10 text-sm border-r-2 border-slate-200 last:border-0 first:border-0${index === 1 ? ' sticky left-0 bg-white z-5' : ''}`}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>

  )
}

const CamerasView = ({ data }: { data: IWebCOOSCameraPageFiltered }): ReactElement => {
  return (
    <div className='flex flex-col h-full'>
      <CameraFilters data={data} />
      <div className='-mx-10'>
        <Tabs
          className='relative'
          navClassName="sticky top-16 md:top-32 z-10 pl-5 bg-white border-b border-slate-200"
          tabNavClassName="cursor-pointer hover:bg-slate-100 border-none"
          activeTabNavClassName="border-none"
          onChange={(id) => {
            const url = new URL(window.location.href)
            url.searchParams.set('tab', id)
            window.history.pushState({}, '', url.toString())
          }}
          selectedTab={new URL(window.location.href).searchParams.get('tab') ?? undefined}
          tabs={
            [
              {
                id: 'table',
                label: 'Table View',
                content: <CamerasTable data={data} />
              },
              {
                id: 'map',
                label: 'Map View',
                className: 'h-[800px] sticky top-35 px-6',
                content: <LoadedMap data={data.assets} includeCamerasInView={true} />
              }
            ]
          }
        />
      </div>

    </div>
  )
}

export default CamerasView